<template>
  <div
    v-if="status"
    v-on-click-outside="onClickOutsideHandler"
    class="relative"
  >
    <div
      class="pill flex max-w-63 items-center justify-center space-x-2 rounded-full px-4 py-1 font-semibold whitespace-nowrap"
      :class="{
        'cursor-pointer': !readonly,
        'cursor-default': readonly,
        'h-6 leading-6 uppercase': tableCell,
      }"
      @click="readonly ? undefined : (showSelector = !showSelector)"
    >
      <span
        class="overflow-hidden text-ellipsis"
        :style="{ color: status.color }"
        :class="fontSize"
      >
        <span v-if="!reduce">{{ scorecardStatusText }}</span>
        <span v-else>{{ scorecardStatusText.charAt(0) }}</span>
      </span>
      <div v-if="!readonly" class="cursor-pointer" @click.stop="handleClick">
        <icon-base
          width="10"
          height="10"
          :icon="IconXSmall"
          :icon-color="status.color"
        />
      </div>
    </div>

    <status-selector-modal
      v-if="showSelector"
      :status-to-edit="status"
      :current-points="currentPoints || 0"
      @update="handleEdit"
      @close="showSelector = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { UTIL_COLORS } from "@/helpers/constants";
import { hexToRgb } from "@/helpers/formatting";

import IconXSmall from "@/components/icons/IconXSmall.vue";
import StatusSelectorModal from "@/components/scorecards/StatusSelectorModal.vue";

import type { IScorecardStatus } from "@/models/scorecards";

const emit = defineEmits<{
  (eventName: "update:modelValue", statuses: IScorecardStatus[]): void;
  (eventName: "remove", id: string | number): void;
}>();

const {
  status,
  readonly = false,
  reduce = false,
  modelValue,
  fontSize = "text-sm",
  tableCell = false,
} = defineProps<{
  status: IScorecardStatus;
  fontSize?: string;
  readonly?: boolean;
  reduce?: boolean;
  modelValue?: IScorecardStatus[] | IScorecardStatus | null;
  currentPoints?: number;
  tableCell?: boolean;
}>();

const { t } = useI18n();

const showSelector = ref(false);

const bgColor = computed(() => {
  const rgbVal = hexToRgb(status?.color || UTIL_COLORS.DEFAULT);
  return `rgba(${rgbVal}, 0.3)`;
});

const scorecardStatusText = computed(() =>
  readonly
    ? status.name
    : t("SCORECARDS.STATUS_DISPLAY", {
        status: status.name,
        from: status.points_from,
        to: status.points_to,
      })
);

const handleEdit = (status: IScorecardStatus) => {
  if (
    readonly ||
    !modelValue ||
    !Array.isArray(modelValue) ||
    !modelValue.length
  ) {
    return;
  }

  const updatedStatusIndex = modelValue.findIndex((st) => st.id === status.id);

  if (updatedStatusIndex === -1) {
    return;
  }

  const payload = modelValue.map((savedStatus) => {
    if (savedStatus.id === status.id) {
      return status;
    }
    return savedStatus;
  });

  emit("update:modelValue", payload);
};

const handleClick = () => {
  emit("remove", status.id);
};

const onClickOutsideHandler = [
  () => (showSelector.value = false),
  { capture: false },
];
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.pill {
  background-color: v-bind(bgColor);
}
</style>
